import { Skeleton } from './Skeleton';

/**
 * Skeleton for the admin dashboard
 */
export function AdminDashboardSkeleton() {
  return (
    <div className="min-h-screen bg-tg-bg">
      {/* Header skeleton */}
      <div className="px-4 py-3 flex items-center gap-3 border-b border-tg-secondary-bg">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-6 rounded w-48 mb-1" />
          <Skeleton className="h-4 rounded w-24" />
        </div>
      </div>

      {/* Quick toggle skeleton */}
      <div className="p-4">
        <Skeleton className="h-24 rounded-2xl w-full" />
      </div>

      {/* Options grid skeleton */}
      <div className="px-4 grid grid-cols-2 gap-3">
        <Skeleton className="h-28 rounded-xl" />
        <Skeleton className="h-28 rounded-xl" />
      </div>

      {/* Menu buttons skeleton */}
      <div className="p-4 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl w-full" />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for menu stock list
 */
export function MenuStockSkeleton() {
  return (
    <div className="min-h-screen bg-tg-bg">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-tg-bg border-b border-tg-secondary-bg">
        <div className="px-4 py-3 flex items-center gap-3">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div>
            <Skeleton className="h-6 rounded w-40 mb-1" />
            <Skeleton className="h-4 rounded w-28" />
          </div>
        </div>
      </div>

      {/* Categories with items */}
      {Array.from({ length: 3 }).map((_, catIdx) => (
        <div key={catIdx} className="mt-4">
          <div className="px-4 py-2">
            <Skeleton className="h-4 rounded w-24" />
          </div>
          <div className="bg-tg-secondary-bg mx-4 rounded-xl overflow-hidden divide-y divide-tg-bg">
            {Array.from({ length: 4 }).map((_, itemIdx) => (
              <div key={itemIdx} className="px-4 py-3 flex items-center justify-between">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 rounded w-32" />
                  <Skeleton className="h-4 rounded w-16" />
                </div>
                <Skeleton className="h-8 rounded-full w-24" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for menu editor main view
 */
export function MenuEditorSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {/* Menu option buttons */}
      <Skeleton className="h-16 rounded-xl w-full" />
      <Skeleton className="h-16 rounded-xl w-full" />
      {/* Add product button */}
      <Skeleton className="h-14 rounded-xl w-full" />
    </div>
  );
}

/**
 * Skeleton for product list in admin
 */
export function ProductListSkeleton() {
  return (
    <div className="min-h-screen bg-tg-bg">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-tg-bg border-b border-tg-secondary-bg">
        <div className="px-4 py-3 flex items-center gap-3">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-6 rounded w-32 mb-1" />
            <Skeleton className="h-4 rounded w-24" />
          </div>
        </div>
        {/* Filter tabs */}
        <div className="px-4 pb-3 flex gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-8 rounded-full w-20" />
          ))}
        </div>
      </div>

      {/* Products */}
      {Array.from({ length: 2 }).map((_, catIdx) => (
        <div key={catIdx} className="mt-4">
          <div className="px-4 py-2 flex items-center justify-between">
            <Skeleton className="h-4 rounded w-24" />
            <Skeleton className="h-4 rounded w-16" />
          </div>
          <div className="bg-tg-secondary-bg mx-4 rounded-xl overflow-hidden divide-y divide-tg-bg">
            {Array.from({ length: 3 }).map((_, itemIdx) => (
              <div key={itemIdx} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 rounded w-40" />
                  <Skeleton className="h-4 rounded w-16" />
                </div>
                <Skeleton className="w-9 h-9 rounded-lg" />
                <Skeleton className="w-9 h-9 rounded-lg" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for statistics view - detailed structure matching StatsView
 */
export function StatsViewSkeleton() {
  return (
    <div className="px-4 pb-8 space-y-6 animate-pulse">
      {/* Summary Cards Skeleton - 3 cards */}
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-tg-secondary-bg rounded-xl p-3 flex flex-col items-center gap-1">
            <Skeleton className="w-5 h-5 rounded" />
            <Skeleton className="h-6 rounded w-12" />
            <Skeleton className="h-3 rounded w-10" />
            <Skeleton className="h-3 rounded w-8" />
          </div>
        ))}
      </div>

      {/* Order Types & Peak Hours Skeleton - 2x2 grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-tg-secondary-bg rounded-xl p-4 space-y-3">
          <Skeleton className="h-4 rounded w-24" />
          <div className="space-y-2">
            <Skeleton className="h-4 rounded w-20" />
            <Skeleton className="h-4 rounded w-20" />
          </div>
        </div>
        <div className="bg-tg-secondary-bg rounded-xl p-4 space-y-3">
          <Skeleton className="h-4 rounded w-24" />
          <div className="space-y-1">
            <Skeleton className="h-4 rounded w-full" />
            <Skeleton className="h-4 rounded w-full" />
            <Skeleton className="h-4 rounded w-full" />
          </div>
        </div>
      </div>

      {/* Chart Skeleton */}
      <div className="bg-tg-secondary-bg rounded-xl p-4">
        <Skeleton className="h-4 rounded w-32 mb-3" />
        <div className="flex items-end gap-1 h-24">
          {[60, 80, 40, 90, 55, 70, 45].map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <Skeleton className="w-full rounded-t" style={{ height: `${h}%` }} />
              <Skeleton className="h-3 w-3 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* Top Products Skeleton */}
      <div className="bg-tg-secondary-bg rounded-xl p-4">
        <Skeleton className="h-4 rounded w-28 mb-3" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 rounded w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-6 rounded" />
                <Skeleton className="h-4 w-12 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Customers Skeleton */}
      <div className="bg-tg-secondary-bg rounded-xl p-4">
        <Skeleton className="h-4 rounded w-24 mb-3" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 rounded w-20" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-10 rounded" />
                <Skeleton className="h-4 w-12 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

